/**
 * ADD A SHOPPING PRODUCT AD — /google-ads/shopping/create/ad[?adgroup_id=…]
 * Product ads carry no creative: Google renders title, image and price from
 * the Merchant Center feed. One per ad group is all that's needed, so the page
 * shows what the group already has before adding another.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Package } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Select } from '@/components/Select';
import { googleAdsService, GoogleAdsAdGroupRow, ShoppingProductGroup } from '@/services/google-ads.service';
import { BackTo, errMsg, formatMicros, Section, Spinner, Tag, useYoutubeAuth } from '@/components/google-ads/youtube/youtubeShared';
import { Callout, ChipList, ConnectFirst, GoalStrip, PageSpinner, SaveBar } from '@/components/google-ads/objectives/wizardChrome';
import { ShoppingPreview } from '@/components/google-ads/shopping/shoppingShared';
import { SHOPPING_OBJECTIVE as CFG } from './shoppingObjective';

export default function CreateShoppingAd() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { auth, accountUid } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const currency = auth?.currency ?? '';
  const [adgroups, setAdgroups] = useState<GoogleAdsAdGroupRow[] | null>(null);
  const [adgroupId, setAdgroupId] = useState(params.get('adgroup_id') || '');
  const [productGroups, setProductGroups] = useState<ShoppingProductGroup[] | null>(null);
  const [existingAds, setExistingAds] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const adgroup = adgroups?.find((g) => g.id === adgroupId);

  useEffect(() => {
    if (!accountUid) return;
    googleAdsService.listAdGroups(accountUid)
      .then((l) => setAdgroups(l.filter((g) => (!g.campaignChannelType || g.campaignChannelType === 'SHOPPING') && g.status !== 'REMOVED')))
      .catch(() => setAdgroups([]));
  }, [accountUid]);

  useEffect(() => {
    setProductGroups(null); setExistingAds(null);
    if (!accountUid || !adgroupId) return;
    let alive = true;
    Promise.all([
      googleAdsService.shopping.productGroups(accountUid, adgroupId).catch(() => [] as ShoppingProductGroup[]),
      googleAdsService.listAds(accountUid, adgroupId).catch(() => []),
    ]).then(([pg, ads]) => { if (alive) { setProductGroups(pg); setExistingAds(ads.filter((a) => a.status !== 'REMOVED').length); } });
    return () => { alive = false; };
  }, [accountUid, adgroupId]);

  const byCampaign = useMemo(() => {
    const m = new Map<string, { name: string; groups: GoogleAdsAdGroupRow[] }>();
    for (const g of adgroups ?? []) {
      const row = m.get(g.campaignId) ?? { name: g.campaignName || `Campaign ${g.campaignId}`, groups: [] };
      row.groups.push(g);
      m.set(g.campaignId, row);
    }
    return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [adgroups]);

  const issues = adgroupId ? [] : ['Pick the ad group.'];

  const submit = async () => {
    if (!accountUid || !adgroupId) return;
    setSaving(true); setError(null);
    try {
      await googleAdsService.shopping.createAd(accountUid, { adGroupId: adgroupId, status: 'PAUSED' });
      toast.success('Product ad created — paused until the campaign is enabled.');
      navigate(CFG.base);
    } catch (e) { setError(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <>
      <Topbar title="New product ad" breadcrumb={`Google Ads / ${CFG.title}`} description="Google builds the ad from your product feed — nothing to design"
        back={<BackTo to={CFG.base} label={`Back to ${CFG.title}`} />} />

      <div className="w-full px-7 py-7">
        {auth === undefined && <PageSpinner />}
        {auth !== undefined && !ready && <ConnectFirst what="product ads" />}

        {ready && (
          <div className="flex w-full gap-5 pb-12">
            <div className="min-w-0 flex-1 space-y-4">
              <GoalStrip items={[
                { label: 'Goal', value: CFG.goal },
                { label: 'Type', value: 'Shopping product ad' },
                { label: 'Adding to', value: adgroup ? `${adgroup.name}${adgroup.campaignName ? ` · ${adgroup.campaignName}` : ''}` : 'pick an ad group' },
              ]}>
                <Tag>starts paused</Tag>
              </GoalStrip>

              <Section title="Ad group" hint="Product ads take their title, image and price from Merchant Center. Google shows the matching product for each search.">
                <Select className="h-10 rounded-lg" value={adgroupId} onChange={(e) => setAdgroupId(e.target.value)}>
                  <option value="">{adgroups === null ? 'Loading ad groups…' : adgroups.length ? 'Pick a Shopping ad group' : 'No Shopping ad groups yet'}</option>
                  {byCampaign.map((c) => (
                    <optgroup key={c.name} label={c.name}>{c.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</optgroup>
                  ))}
                </Select>
                {adgroupId && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-baseline justify-between">
                      <span className="text-2xs font-semibold uppercase tracking-wide text-text-muted">Product groups</span>
                      {existingAds !== null && <span className="text-2xs text-text-muted">{existingAds} product ad{existingAds === 1 ? '' : 's'} already</span>}
                    </div>
                    {productGroups === null ? <span className="flex items-center gap-1.5 text-2xs text-text-muted"><Spinner size={11} /> Loading…</span>
                      : <ChipList items={productGroups.map((p) => `${p.label}${p.cpcBidMicros && Number(p.cpcBidMicros) > 0 ? ` · ${formatMicros(p.cpcBidMicros, currency)}` : ''}`)} empty="No product group — the ad can't serve until one exists" />}
                  </div>
                )}
              </Section>

              {existingAds !== null && existingAds > 0 && (
                <Callout tone="info" icon={Package}>This ad group already has a product ad. Shopping only needs one per group — a second one adds nothing unless the first was removed.</Callout>
              )}

              <SaveBar issues={issues} error={error} saving={saving} onSave={submit} onCancel={() => navigate(CFG.base)} label="Create product ad" />
            </div>

            <div className="hidden w-[320px] shrink-0 xl:block">
              <div className="sticky top-24">
                <ShoppingPreview merchantId="" merchantLinked={null} groups={adgroupId ? 1 : 0} withAds={existingAds ? 1 : 0} local={false} currency={currency} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
